import { isClosedCycle } from './cycleFills';
import type { DrawingDocument, GraphNode, NormalizedPoint } from '../model';

// Stored coordinates are rounded to COORDINATE_DECIMAL_PLACES, then later
// re-normalized (divided by canvas width/height) for "same point" checks.
// That round-trip's quantization error must stay well under this tolerance
// or genuinely-identical points (e.g. a fused ring's shared bond endpoint)
// stop matching, even on the smallest supported canvas.
const POINT_TOLERANCE = 1e-3;
const COORDINATE_DECIMAL_PLACES = 2;
let localIdSequence = 0;

export function createId(prefix: string): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return prefix + '-' + uuid;
  localIdSequence += 1;
  return [prefix, 'local', globalThis.performance.timeOrigin, localIdSequence].join('-');
}

/**
 * Derives the next id in a `${prefix}${n}` sequence from the highest number
 * already in use, so a deleted entity's number is never reused and later
 * additions can't collide with survivors.
 */
export function nextSequentialId(existingIds: Iterable<string>, prefix: string): string {
  const pattern = new RegExp(`^${prefix}(\\d+)$`);
  let max = 0;
  for (const id of existingIds) {
    const match = pattern.exec(id);
    if (match) max = Math.max(max, Number(match[1]));
  }
  return prefix + (max + 1);
}

export function createSequentialIds(count: number, prefix: string): string[] {
  return Array.from({ length: count }, (_, index) => `${prefix}${index + 1}`);
}

export function roundCoordinate(value: number): number {
  const scale = 10 ** COORDINATE_DECIMAL_PLACES;
  return Math.round(value * scale) / scale;
}

interface ChangeStructureOptions {
  readonly clearAromaticity?: boolean;
  // Drop stereo (double-bond config + crossed/wedge style) for one edge only -- a bond whose
  // order just changed can no longer carry its old stereo, but every *other* bond's wedge /
  // crossed-double is left alone.
  readonly clearStereoForEdge?: string;
  // Drop stereo for one node: its tetrahedral config, and the double-bond config + wedge /
  // crossed-double style of every edge touching it -- e.g. retyping an atom or changing its
  // charge can invalidate its own stereocentre and its incident bonds, but not the rest of the
  // drawing. (There is deliberately no "clear all stereo" option -- a topology replace does its
  // own clearing in replaceTopology; every targeted edit names the edge or node it touches.)
  readonly clearStereoForNode?: string;
  // Retyping an atom invalidates chemical assignments, not the authored wedge geometry (#1071).
  readonly preserveWedgeStyles?: boolean;
}

function omitKeys<T>(entries: Readonly<Record<string, T>>, drop: ReadonlySet<string>): Record<string, T> {
  return Object.fromEntries(Object.entries(entries).filter(([id]) => !drop.has(id)));
}

// Resolves how a structure change touches stereo: nothing, a global wipe, or a targeted clear of
// one edge / one node (and its incident edges). CIP is dropped by every non-empty case.
function resolveStereoClear(
  document: DrawingDocument,
  graph: DrawingDocument['structure']['graph'],
  baseStereochemistry: DrawingDocument['structure']['stereochemistry'],
  options: ChangeStructureOptions
): {
  stereochemistry: DrawingDocument['structure']['stereochemistry'];
  bondStyles: DrawingDocument['depiction']['bondStyles'];
  clearsCip: boolean;
} {
  if (!options.clearStereoForEdge && !options.clearStereoForNode) {
    return { stereochemistry: baseStereochemistry, bondStyles: document.depiction.bondStyles, clearsCip: false };
  }
  const clearedEdgeIds = new Set<string>();
  if (options.clearStereoForEdge) clearedEdgeIds.add(options.clearStereoForEdge);
  if (options.clearStereoForNode) {
    for (const edge of graph.edges) {
      if (edge.source === options.clearStereoForNode || edge.target === options.clearStereoForNode) {
        clearedEdgeIds.add(edge.id);
      }
    }
  }
  const tetrahedral = options.clearStereoForNode
    ? omitKeys(baseStereochemistry.tetrahedral, new Set([options.clearStereoForNode]))
    : baseStereochemistry.tetrahedral;
  return {
    stereochemistry: { tetrahedral, doubleBonds: omitKeys(baseStereochemistry.doubleBonds, clearedEdgeIds) },
    bondStyles: Object.fromEntries(
      Object.entries(document.depiction.bondStyles).filter(
        ([id, style]) =>
          !clearedEdgeIds.has(id) ||
          (options.preserveWedgeStyles && (style.style === 'solid-wedge' || style.style === 'hashed-wedge'))
      )
    ),
    clearsCip: true
  };
}

export function changeStructure(
  document: DrawingDocument,
  changes: Partial<DrawingDocument['structure']>,
  options: ChangeStructureOptions = {}
): DrawingDocument {
  const revision = document.structure.revision + 1;
  const graph = changes.graph ?? document.structure.graph;
  const { stereochemistry, bondStyles, clearsCip } = resolveStereoClear(
    document,
    graph,
    changes.stereochemistry ?? document.structure.stereochemistry,
    options
  );
  return {
    ...document,
    structure: { ...document.structure, ...changes, revision, graph, stereochemistry },
    depiction: {
      ...document.depiction,
      structureRevision: revision,
      coordinateSet: { ...document.depiction.coordinateSet, structureRevision: revision },
      bondStyles
    },
    derivedPropertySets: document.derivedPropertySets
      // clearAromaticity marks an atom/bond-composition change, which also invalidates any
      // previously-computed partial charges (unlike stereo perception, which doesn't).
      .filter(
        (result) =>
          !(options.clearAromaticity && (result.kind === 'aromaticity' || result.kind === 'partial-charge')) &&
          !(clearsCip && result.kind === 'cip')
      )
      .map((result) => ({ ...result, structureRevision: revision }))
  };
}

export function changeDepiction(
  document: DrawingDocument,
  changes: Partial<DrawingDocument['depiction']>
): DrawingDocument {
  return {
    ...document,
    depiction: { ...document.depiction, ...changes, revision: document.depiction.revision + 1 }
  };
}

function withoutBrokenHaptoBonds(
  graph: DrawingDocument['structure']['graph'],
  bonds: DrawingDocument['structure']['bonds']
): Pick<DrawingDocument['structure'], 'graph' | 'bonds'> {
  const nodeIds = new Set(graph.nodes.map((node) => node.id));
  const broken = new Set(
    Object.entries(bonds).flatMap(([id, bond]) =>
      bond.ligandAtomIds?.some((atomId) => !nodeIds.has(atomId)) ? [id] : []
    )
  );
  if (broken.size === 0) return { graph, bonds };
  return {
    graph: { ...graph, edges: graph.edges.filter((edge) => !broken.has(edge.id)) },
    bonds: Object.fromEntries(Object.entries(bonds).filter(([id]) => !broken.has(id)))
  };
}

export function replaceTopology(
  document: DrawingDocument,
  givenGraph: DrawingDocument['structure']['graph'],
  atoms: DrawingDocument['structure']['atoms'],
  givenBonds: DrawingDocument['structure']['bonds'],
  formalCharges: DrawingDocument['structure']['formalCharges'],
  coordinates: DrawingDocument['depiction']['coordinateSet']['coordinates'],
  atomLabels: DrawingDocument['depiction']['atomLabels'],
  bondLengthOverrides: DrawingDocument['depiction']['bondLengthOverrides']
): DrawingDocument {
  const structureRevision = document.structure.revision + 1;
  // ADR 0026: a hapto bond goes with any of its ligand atoms -- what's left isn't the ligand drawn.
  const { graph, bonds } = withoutBrokenHaptoBonds(givenGraph, givenBonds);
  // A topology replace adds/removes nodes and edges; the ones that remain keep their identity, so
  // user-authored per-edge / per-node depiction and stereo (wedge & crossed-double bond styles,
  // double-bond and tetrahedral stereochemistry) carry over -- filtered to the survivors, the
  // same way the caller already filters atomLabels / bondLengthOverrides. Only entries for
  // removed edges/nodes are dropped. Adding a bond no longer wipes an existing crossed double
  // bond or a wedge elsewhere in the drawing.
  const nodeIds = new Set(graph.nodes.map((node) => node.id));
  const edgeIds = new Set(graph.edges.map((edge) => edge.id));
  const keepByEdge = <T>(entries: Readonly<Record<string, T>>): Record<string, T> =>
    Object.fromEntries(Object.entries(entries).filter(([id]) => edgeIds.has(id)));
  const keepByNode = <T>(entries: Readonly<Record<string, T>>): Record<string, T> =>
    Object.fromEntries(Object.entries(entries).filter(([id]) => nodeIds.has(id)));
  return {
    ...document,
    structure: {
      ...document.structure,
      revision: structureRevision,
      graph,
      atoms,
      bonds,
      formalCharges,
      stereochemistry: {
        tetrahedral: keepByNode(document.structure.stereochemistry.tetrahedral),
        doubleBonds: keepByEdge(document.structure.stereochemistry.doubleBonds)
      }
    },
    depiction: {
      ...document.depiction,
      revision: document.depiction.revision + 1,
      structureRevision,
      coordinateSet: {
        ...document.depiction.coordinateSet,
        structureRevision,
        coordinates,
        ...(document.depiction.coordinateSet.depths
          ? { depths: keepByNode(document.depiction.coordinateSet.depths) }
          : {})
      },
      atomLabels,
      bondStyles: keepByEdge(document.depiction.bondStyles),
      bondLengthOverrides,
      ...(document.depiction.cycleFills
        ? { cycleFills: document.depiction.cycleFills.filter((fill) => isClosedCycle(fill.atomIds, graph.edges)) }
        : {}),
      // #868 -- per-atom / per-bond appearance follows its atom or bond, like the maps above.
      ...(document.depiction.atomAppearance ? { atomAppearance: keepByNode(document.depiction.atomAppearance) } : {}),
      ...(document.depiction.bondAppearance ? { bondAppearance: keepByEdge(document.depiction.bondAppearance) } : {})
    },
    // #933 (owner, 2026-10-11): an arrow anchored to an atom or bond that's gone goes with it --
    // nothing points at nothing. Undo restores both (history keeps whole documents).
    annotations: document.annotations.filter((annotation) => !anchoredToRemoved(annotation, nodeIds, edgeIds)),
    // aromaticity, cip, and partial-charge are whole-molecule perception results, not per-edge
    // authoring -- a topology change invalidates all of them, so they are cleared outright.
    derivedPropertySets: []
  };
}

type Annotation = DrawingDocument['annotations'][number];

/** #933: whether an arrow has an end anchored to an atom or bond no longer in the graph. */
function anchoredToRemoved(
  annotation: Annotation,
  nodeIds: ReadonlySet<string>,
  edgeIds: ReadonlySet<string>
): boolean {
  if (annotation.kind !== 'curved-arrow' && annotation.kind !== 'reaction-arrow') return false;
  return [annotation.from, annotation.to].some((anchor) =>
    anchor.kind === 'atom' || anchor.kind === 'lone-pair'
      ? !nodeIds.has(anchor.atomId)
      : anchor.kind === 'bond'
        ? !edgeIds.has(anchor.bondId)
        : false
  );
}

export function withoutKey<T>(values: Readonly<Record<string, T>>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(values).filter(([candidate]) => candidate !== key));
}

export function samePoint(first: NormalizedPoint, second: NormalizedPoint): boolean {
  return Math.abs(first.x - second.x) <= POINT_TOLERANCE && Math.abs(first.y - second.y) <= POINT_TOLERANCE;
}

export function nodeAtPoint(document: DrawingDocument, point: NormalizedPoint): GraphNode | undefined {
  const coordinateSet = document.depiction.coordinateSet;
  return document.structure.graph.nodes.find((node) => {
    const coordinate = coordinateSet.coordinates[node.id];
    return (
      coordinate &&
      samePoint(point, {
        x: coordinate.x / coordinateSet.width,
        y: coordinate.y / coordinateSet.height
      })
    );
  });
}
